import { Source, Layer } from 'react-map-gl/maplibre';

/**
 * Faults, shear zones and fold axial traces from GSWA, as cached vector tiles.
 *
 * Served by our own GeoServer from the GSWA linear-structure datasets held in
 * PostGIS (goldfinder-geoserver: scripts/fetch-linear-structures.py,
 * sql/faults-tiles-views.sql, scripts/register-local-faults-layers.sh):
 *
 *   ne:wa_faults_500k_tiles   DMIRS-015, 1:500 000 - drawn from z6
 *   ne:wa_faults_100k_tiles   DMIRS-053, 1:100 000 - drawn from z10
 *
 * This replaces a live SLIP query per pan, which returned every structure type
 * (70% bedding/foliation trends), capped at 1,000 features, ungeneralised, with
 * no cache - unreadable when zoomed out and incomplete when zoomed in.
 *
 * The 1:100k mapping is not statewide (none around Yalgoo or Wiluna, even on
 * SLIP). Every 1:500k line carries covered_by_100k - whether its 0.5-degree
 * cell has 1:100k mapping - and from z10 the 1:500k lines are drawn only where
 * that is false, so the gaps keep their regional structures instead of going
 * blank, and nowhere draws both.
 *
 * Tile layers are TMS (Y flipped: scheme 'tms'); the MVT source-layer name is
 * the GeoServer layer name without the workspace (goldfinder-geoserver
 * LAYERS.md section 3).
 */
const TILES_BASE = (process.env.REACT_APP_FAULTS_TILES_BASE
  || (process.env.NODE_ENV === 'development'
    ? 'http://localhost:8600/geoserver'
    : process.env.REACT_APP_GOLD_FINDER_GEOSERVER) || '').replace(/\/$/, '');

const tmsUrl = (layer) =>
  `${TILES_BASE}/gwc/service/tms/1.0.0/ne%3A${layer}@EPSG%3A900913@pbf/{z}/{x}/{y}.pbf`;

const S500 = { id: 'wa-faults-500k', layer: 'wa_faults_500k_tiles', minzoom: 4, maxzoom: 16 };
const S100 = { id: 'wa-faults-100k', layer: 'wa_faults_100k_tiles', minzoom: 10, maxzoom: 16 };

/** Zoom the 1:500k regional structures first appear. */
const MIN_ZOOM = 6;
/** From here the 1:100k mapping takes over (1:500k only in its gaps). */
const DETAIL_ZOOM = 10;

const COLOURS = {
  fault: '#111827',     // faults and shear zones
  concealed: '#374151',
  fold: '#6D28D9',      // fold axial traces - not a fault, so not the same ink
};

const lineColour = ['case', ['==', ['get', 'kind'], 'fold'], COLOURS.fold, COLOURS.fault];
// Major structures read heavier at every zoom.
const lineWidth = (k) => ['interpolate', ['linear'], ['zoom'],
  MIN_ZOOM, ['case', ['==', ['get', 'major'], true], 1.4 * k, 0.6 * k],
  14, ['case', ['==', ['get', 'major'], true], 2.8 * k, 1.6 * k]];

const EXPOSED = ['!=', ['get', 'concealed'], true];
const CONCEALED = ['==', ['get', 'concealed'], true];
const IN_100K_GAP = ['!=', ['get', 'covered_by_100k'], true];

/**
 * The style layers for one source and zoom band. `filter` narrows the band
 * (the 1:500k gap-fill). line-dasharray is not data-driven in MapLibre, so
 * exposed and concealed are separate filtered layers - getting that wrong makes
 * a layer fail to mount at all.
 */
function bandLayers(src, idSuffix, { minzoom, maxzoom, filter }, vis) {
  const f = (extra) => (filter ? ['all', filter, extra] : extra);
  const common = { source: src.id, 'source-layer': src.layer, minzoom, ...(maxzoom ? { maxzoom } : null) };
  return [
    // Casing underneath keeps the line readable over busy geology rasters.
    <Layer key={`c${idSuffix}`} id={`${src.id}-casing${idSuffix}`} type="line" {...common}
           {...(filter ? { filter } : null)}
           paint={{ 'line-color': '#ffffff', 'line-width': lineWidth(2.2), 'line-opacity': 0.6 }}
           layout={{ ...vis, 'line-cap': 'round' }} />,
    <Layer key={`l${idSuffix}`} id={`${src.id}-line${idSuffix}`} type="line" {...common}
           filter={f(EXPOSED)}
           paint={{ 'line-color': lineColour, 'line-width': lineWidth(1) }}
           layout={{ ...vis, 'line-cap': 'round' }} />,
    <Layer key={`d${idSuffix}`} id={`${src.id}-line-concealed${idSuffix}`} type="line" {...common}
           filter={f(CONCEALED)}
           paint={{ 'line-color': ['case', ['==', ['get', 'kind'], 'fold'], COLOURS.fold, COLOURS.concealed],
             'line-width': lineWidth(0.9), 'line-dasharray': [3, 2] }}
           layout={{ ...vis, 'line-cap': 'round' }} />,
  ];
}

/** Layers a click can land on (gl-map-optimized interactiveLayerIds). */
export const WA_FAULT_LAYER_IDS = [
  `${S500.id}-line`, `${S500.id}-line-concealed`,
  `${S500.id}-line-gap`, `${S500.id}-line-concealed-gap`,
  `${S100.id}-line`, `${S100.id}-line-concealed`,
];

/** Which map scale a clicked fault feature came from. */
export const faultScale = (feature) => (feature?.source === S100.id ? '1:100 000' : '1:500 000');

export function WaGeologyOverlay({ overlayVisibility }) {
  if (!TILES_BASE) return null;
  const vis = { visibility: overlayVisibility.waFaults ? 'visible' : 'none' };

  return (
    <>
      <Source id={S500.id} type="vector" scheme="tms" tiles={[tmsUrl(S500.layer)]}
              minzoom={S500.minzoom} maxzoom={S500.maxzoom} attribution="DEMIRS / GSWA">
        {/* every 1:500k structure, zoomed out */}
        {bandLayers(S500, '', { minzoom: MIN_ZOOM, maxzoom: DETAIL_ZOOM }, vis)}
        {/* zoomed in, only where the 1:100k mapping does not reach */}
        {bandLayers(S500, '-gap', { minzoom: DETAIL_ZOOM, filter: IN_100K_GAP }, vis)}
      </Source>
      <Source id={S100.id} type="vector" scheme="tms" tiles={[tmsUrl(S100.layer)]}
              minzoom={S100.minzoom} maxzoom={S100.maxzoom} attribution="DEMIRS / GSWA">
        {bandLayers(S100, '', { minzoom: DETAIL_ZOOM }, vis)}
      </Source>
    </>
  );
}

export default WaGeologyOverlay;
